/**
 * Bewertungsbitte nach dem Projektabschluss (Migration 192, kontor_bewertung.py).
 *
 * Der Server legt beim Abschluss eine Aufgabe an; hier wird die fertige Mail
 * geholt (Standardtext + Claras ein, zwei Sätze), nach dem Senden erledigt,
 * und die zuletzt abgeschlossenen Projekte lassen sich nachholen.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost } from "@vimaana/ui";

export type BewertungEntwurf = {
  id: string;
  status: "offen" | "gesendet" | "verzichtet";
  projekt_id: string;
  kunde_id: string;
  an: string;
  betreff: string;
  text: string;
  /** fertig = Claras Sätze stehen drin · unterwegs = der Evo rechnet · aus = Evo meldet sich nicht · keine = ohne Clara */
  clara: "fertig" | "unterwegs" | "aus" | "keine";
  /** Nur wenn Claras Sätze im Text stehen — geht beim Senden in die KI-Spur. */
  auftrag_id: string;
  titel: string;
};

export type BewertungEinstellung = {
  link: string;
  betreff: string;
  text: string;
  eigen: boolean;
  platzhalter: string[];
  standard: { betreff: string; text: string };
};

export type NachholProjekt = {
  projekt_id: string;
  titel: string;
  abschluss: string;
  kunde_id: string;
  kunde: string;
  hat_mail: boolean;
  pflege: boolean;
};

/** Die Mail zur Bitte. Solange Clara rechnet, wird nachgefragt (höchstens gut eine Minute, dann Aufgabe der Oberfläche). */
export function useBewertungEntwurf(id: string, nachfragen: boolean) {
  return useQuery<BewertungEntwurf>({
    queryKey: ["bewertung", "entwurf", id],
    queryFn: () => apiGet(`/api/kontor/bewertung/${id}/entwurf`),
    refetchInterval: (q) => (nachfragen && q.state.data?.clara === "unterwegs" ? 4_000 : false),
    staleTime: 0,
    gcTime: 0,
  });
}

function neuLaden(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ["notizen"] });
  qc.invalidateQueries({ queryKey: ["bewertung"] });
}

export function useBewertungGesendet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiPost(`/api/kontor/bewertung/${id}/gesendet`, {}),
    onSuccess: () => neuLaden(qc),
  });
}

export function useBewertungVerzichten() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiPost(`/api/kontor/bewertung/${id}/verzichten`, {}),
    onSuccess: () => neuLaden(qc),
  });
}

export function useBewertungNachholen() {
  return useQuery<{ tage: number; erledigt: boolean; projekte: NachholProjekt[] }>({
    queryKey: ["bewertung", "nachholen"],
    queryFn: () => apiGet("/api/kontor/bewertung/nachholen"),
    staleTime: 60_000,
  });
}

export function useBewertungNachholenAnlegen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (projekt_ids: string[]) =>
      apiPost<{ angelegt: number; ergebnisse: { projekt_id: string; angelegt: boolean; grund: string }[] }>(
        "/api/kontor/bewertung/nachholen", { projekt_ids },
      ),
    onSuccess: () => neuLaden(qc),
  });
}

export function useBewertungEinstellung() {
  return useQuery<BewertungEinstellung>({
    queryKey: ["bewertung", "einstellung"],
    queryFn: () => apiGet("/api/kontor/bewertung/einstellung"),
    staleTime: 5 * 60_000,
  });
}

export function useBewertungEinstellungSpeichern() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { link: string; betreff: string; text: string }) =>
      apiPost<BewertungEinstellung>("/api/kontor/bewertung/einstellung", body),
    onSuccess: (d) => qc.setQueryData(["bewertung", "einstellung"], d),
  });
}
